import { AddIcon } from '../../../components/ui/icons';
import { surfaceShadows } from '../../../surfaceStyles';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { ErrorState, LoadingState } from '../../../components/ui/States';

export const COLLECTION_PAGE_MAX_WIDTH = 1200;

export function CollectionPage({
  title,
  fullWidth = false,
  stickyTop = 0,
  shellHeader,
  toolbar,
  header,
  beforeContent,
  loading,
  loadingIndicatorInHeader = false,
  error,
  empty,
  children,
  addLabel,
  onAdd,
  phoneLayout,
}: {
  title: string;
  fullWidth?: boolean;
  stickyTop?: number;
  shellHeader?: React.ReactNode;
  toolbar: React.ReactNode;
  header: React.ReactNode;
  beforeContent?: React.ReactNode;
  loading?: boolean;
  loadingIndicatorInHeader?: boolean;
  error?: string | null;
  empty?: React.ReactNode;
  children: React.ReactNode;
  addLabel: string;
  onAdd?: () => void;
  phoneLayout: boolean;
}) {
  const titleId = `${title.toLocaleLowerCase()}-page-title`;
  const hasChildren = React.Children.count(children) > 0;
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const retractableRef = useRef<HTMLDivElement>(null);
  const [headerStuck, setHeaderStuck] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const scrollUpperHeader = phoneLayout && Boolean(shellHeader);
  const showHeaderShadow = headerStuck;

  useEffect(() => {
    if (!scrollUpperHeader) return;
    const retractable = retractableRef.current;
    if (!retractable) return;
    const scrollPosition = () =>
      Math.max(
        0,
        Math.min(
          window.scrollY,
          Math.max(
            0,
            document.documentElement.scrollHeight - window.innerHeight,
          ),
        ),
      );
    let previousY = scrollPosition();
    let offset = -previousY;
    let height = 0;
    let frame: number | null = null;
    const measure = () => {
      height = retractable.getBoundingClientRect().height;
      offset = Math.max(-height, Math.min(0, offset));
      setHeaderOffset(offset);
    };
    const onScroll = () => {
      const y = scrollPosition();
      offset =
        y === 0 ? 0 : Math.max(-height, Math.min(0, offset + previousY - y));
      previousY = y;
      if (frame === null)
        frame = window.requestAnimationFrame(() => {
          frame = null;
          setHeaderOffset(offset);
        });
    };
    measure();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    observer?.observe(retractable);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [scrollUpperHeader]);

  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => setHeaderStuck(!entry.isIntersecting),
      { rootMargin: `-${stickyTop}px 0px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [stickyTop]);

  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      sx={{
        width: '100%',
        maxWidth: fullWidth ? 'none' : COLLECTION_PAGE_MAX_WIDTH,
        mx: 'auto',
      }}
    >
      <Typography id={titleId} component="h1" sx={visuallyHidden}>
        {title}
      </Typography>
      <Box ref={stickySentinelRef} aria-hidden sx={{ height: 1, mb: '-1px' }} />
      <Box
        data-testid="collection-sticky-header"
        data-stuck={headerStuck}
        data-shadow-visible={showHeaderShadow}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: shellHeader ? 0 : 1,
          position: 'sticky',
          // Keep the header's flow height stable for virtualized results. A
          // negative sticky offset lets the upper rows scroll out of view.
          top: scrollUpperHeader
            ? `calc(${stickyTop + headerOffset}px + env(safe-area-inset-top))`
            : stickyTop,
          bgcolor: 'background.paper',
          zIndex: 2,
          isolation: 'isolate',
          py: shellHeader ? 0 : 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              `0 0 0 100vmax ${theme.palette.background.paper}`,
            clipPath: 'inset(0 -100vmax)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 'calc(50% - 50vw)',
            bottom: 0,
            width: '100vw',
            top: 0,
            clipPath: 'inset(0 -16px -16px)',
            pointerEvents: 'none',
            boxShadow: showHeaderShadow ? surfaceShadows.navigation : 'none',
          },
        }}
      >
        <Box ref={retractableRef}>
          {shellHeader}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              pt: shellHeader ? 2 : 0,
              pb: shellHeader ? 1 : 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>{toolbar}</Box>
            {!phoneLayout && onAdd && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAdd}
                aria-label={addLabel}
                sx={{ flexShrink: 0 }}
              >
                {addLabel}
              </Button>
            )}
          </Box>
        </Box>
        <Box sx={{ py: 0.5 }}>{header}</Box>
      </Box>
      {beforeContent}
      <Box sx={{ pt: 1 }}>
        {loading ? (
          loadingIndicatorInHeader ? null : (
            <LoadingState label={`Loading ${title.toLocaleLowerCase()}…`} />
          )
        ) : (
          <>
            {error && <ErrorState message={error} />}
            {hasChildren ? children : empty}
          </>
        )}
      </Box>
    </Box>
  );
}
